/** Text color on the accent (`--brand-foreground`): white unless the accent is too light for it. */
export const LIGHT_FOREGROUND = 'oklch(0.99 0 0)';
export const DARK_FOREGROUND = 'oklch(0.21 0.006 285.885)';

/** WCAG AA for normal text. */
const MIN_CONTRAST = 4.5;

type Rgb = readonly [number, number, number];

function parseHex(color: string): Rgb | null {
  const match = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/iu.exec(color.trim());
  if (!match?.[1]) return null;
  const hex = match[1];
  const full =
    hex.length <= 4 ? hex.slice(0, 3).replace(/[0-9a-f]/giu, (digit) => digit + digit) : hex.slice(0, 6);
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

/** sRGB of any CSS color the browser understands (hex, rgb(), hsl(), oklch()…), via a 1×1 canvas. */
function toRgb(color: string): Rgb | null {
  const hex = parseHex(color);
  if (hex) return hex;
  if (typeof document === 'undefined') return null;
  try {
    const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.fillStyle = '#000';
    context.fillStyle = color;
    context.fillRect(0, 0, 1, 1);
    const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
    return r === undefined || g === undefined || b === undefined ? null : [r, g, b];
  } catch {
    return null;
  }
}

/** WCAG relative luminance (0 = black, 1 = white). */
function luminance([r, g, b]: Rgb): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Contrast ratio between white text and `color` (1–21). */
export function contrastWithWhite(color: string): number | null {
  const rgb = toRgb(color);
  return rgb ? 1.05 / (luminance(rgb) + 0.05) : null;
}

/**
 * Foreground for text on `accent` (buttons, the selected navigation item, badges): white while it reaches AA
 * contrast (4.5:1), otherwise a near-black — an administrator may pick a light accent such as yellow or cyan.
 */
export function accentForeground(accent: string): string {
  const contrast = contrastWithWhite(accent);
  return contrast === null || contrast >= MIN_CONTRAST ? LIGHT_FOREGROUND : DARK_FOREGROUND;
}
